





































































































































import { SimulatorState, TSimulatorSnapshot } from '@/simulator';
import { zip } from '@/assets/functions';
import { TransferNode } from '@/syntax';
import { IExplanation, TAssembled } from '@/syntax/types';
import { flagExplain, flagName, flagTitle, regExplain, Register, regName, regTitle, Flag } from "@/constants";
import { explain, TSnapshotExplanation } from "@/explainer";
import { highlight, languages } from 'prismjs';
import Vue from 'vue';
import { BModal } from 'bootstrap-vue';
import debug from "./debug.vue";
import { nextTick } from 'vue/types/umd';

/**
 * Extends IExplanation interface to include a portion of the instruction bitcode,
 * making it into an "ISection".
 */
interface ISection extends IExplanation {
  bits: number[];
}

enum TipType {
  INSTRUCTION = "instruction",
  SECTION = "section",
  DEFAULT = "default"
}

type TTip = {
  title: string;
  subtitle: string;
  detail: string;
  type: TipType;
}

export default Vue.extend({
  name: 'instruction',
  components: {
    debug
  },
  data() {
    return {
      regName,
      regTitle,
      regExplain,

      flagName, 
      flagTitle,
      flagExplain,

      default: {
        bitcode: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        explanation: [{
          title: "No Instruction",
          subtitle: "",
          detail: "Execute an instruction to examine its machine code.",
          range: 32,
        }]
      } as TAssembled,

      tooltip: undefined as TTip | undefined,

      historyShown: false,
      debuggerTooltip: undefined as string | undefined
    }
  },
  computed: {
    paused: SimulatorState.paused,
    delay: SimulatorState.delay,
    currentInstruction: SimulatorState.currentInstruction,
    wasExecuted: SimulatorState.wasExecuted,
    currentTick: SimulatorState.currentTick,
    memSize: () => SimulatorState.memory().size,

    // hack so we can access the enum in dom
    TipType: () => TipType,

    sections: function (): ISection[] {
      const assembled = this.currentInstruction?.assemble() ?? this.default;

      let startIdx = 0;
      return assembled.explanation.map(explanation => {
        const section = {
          ...explanation,
          bits: assembled.bitcode.slice(startIdx, startIdx + explanation.range)
        };
        
        startIdx += explanation.range;
        return section;
      });
    },

    computedTooltip: function (): TTip {
      if (this.tooltip) return this.tooltip;
      if (!this.currentInstruction) return {
        ...this.default.explanation[0],
        type: TipType.DEFAULT
      }

      return {
        title: highlight(this.currentInstruction.text, languages.armv7, 'ARMv7'),
        subtitle: this.wasExecuted ? "Executed" : "Not Executed",
        detail: "This is the assembled machine code for the last instruction. Hover over the different sections to see what they mean.",
        type: TipType.INSTRUCTION
      }
    },

    snapshots: function (): TSnapshotExplanation[] {
      return SimulatorState.snapshots()
        .data()
        .slice()
        .reduce((a, snapshot, index, snapshots) => {
          const prevSnapshot: TSimulatorSnapshot | undefined = snapshots[index - 1];

          a.push(explain(snapshot, prevSnapshot));
          return a;
        }, [] as TSnapshotExplanation[])
        .reverse()
    }
  },
  methods: {
    tip: function (section: ISection) {
      this.tooltip = {
        ...section,
        type: TipType.SECTION
      };
    },

    untip: function () {
      this.tooltip = undefined;
    },

    showHistory: function () {
      this.historyShown = false;
			(this.$refs.history as BModal).show();
      
			const v = this;
			setTimeout(() => {
				v.historyShown = true;
        this.scrollToTick(this.currentTick);
			}, 350);
    },

    scrollToTick(tick: number, behavior: string = "smooth") {
      this.$nextTick(() => {
        const snapshot = (this.$refs[`tick_${tick}`] as any[])?.[0];
        if (snapshot) {
          snapshot.scrollIntoView({ behavior, block: "start", inline: "nearest" });
        }
      });
    },

    reinstate: function (tick: number) {
      SimulatorState.reinstateSnapshot(tick);
    }
  },

  watch: {
    currentTick: {
      handler: function (tick: number) {
        this.$nextTick(() => {
          const scrollBehaviour = this.paused || this.delay > 100 ? "smooth" : "instant";
          this.scrollToTick(tick, scrollBehaviour);
        });
      }
    }
  }
})
